import { ERouterList } from '@internal/router/constants/router-list'
import type { IRouteContext } from '@shared/types/route-context'
import type { RouteParams } from 'vue-router'
import authorization from '@shared/utils/authorization'
import type { CognitoUserSession } from 'amazon-cognito-identity-js'

import { EUserRole } from '@shared/api/user-management/resources/user/constants/user-roles'
import { EDomainScopedRoles } from '@shared/types/constants/domain-scoped-roles'
import { getCommunity } from '@shared/api/user-management/community-controller'

import { parseJwt } from '@shared/utils/helpers'

const getDomainScopedRoles = (token: string) => {
  const parsedJwt = parseJwt(token)
  return JSON.parse(parsedJwt!.permissions).domainScopedRoles
}

const getOrganizationRoleList = (
  domainScopedRoles: { [key in keyof typeof EDomainScopedRoles]: { [key: string]: EUserRole[] } },
  id: string
) => {
  const organizationList = domainScopedRoles[EDomainScopedRoles.ORGANIZATION]
  return (organizationList && organizationList[id]) || []
}

const getLocationRoleList = (
  domainScopedRoles: { [key in keyof typeof EDomainScopedRoles]: { [key: string]: EUserRole[] } },
  locationId: string
) => {
  const locationList = domainScopedRoles[EDomainScopedRoles.COMMUNITY]
  return (locationList && locationList[locationId]) || []
}

const locationOrganizationIsEqual = async (id: string) => {
  const locationIdList = Object.keys(authorization.userLocationList)
  const location = await getCommunity({ communityId: locationIdList[0] })
  return location.organizationId === id
}

const hasAccess = async (sessionToken: string | null, { id, locationId }: RouteParams): Promise<boolean> => {
  if (!sessionToken) {
    return false
  }
  const domainScopedRoles = getDomainScopedRoles(sessionToken)
  const organizationRoleList = getOrganizationRoleList(domainScopedRoles, id as string)
  const locationRoleList = getLocationRoleList(domainScopedRoles, locationId as string)
  if (authorization.hasAccess([EUserRole.PRACTICE_ADMIN, EUserRole.PRACTICE_CLINICIAN, EUserRole.COMMUNITY_ADMIN])) {
    return true
  }
  if (locationId) {
    return (
      organizationRoleList.includes(EUserRole.ORGANIZATION_ADMIN) ||
      locationRoleList.includes(EUserRole.COMMUNITY_ADMIN) ||
      locationRoleList.includes(EUserRole.COMMUNITY_RESIDENT_MANAGEMENT)
    )
  }
  if (id) {
    if (authorization.hasAccess([EUserRole.COMMUNITY_ADMIN])) {
      return await locationOrganizationIsEqual(id as string)
    }
    return (
      organizationRoleList.includes(EUserRole.ORGANIZATION_ADMIN) || organizationRoleList.includes(EUserRole.MEMBER)
    )
  }
  return false
}

export const permissions = async ({ next, to }: IRouteContext) => {
  const session: CognitoUserSession | null = await authorization.loadPersisted()
  const customToken = localStorage.getItem('customToken') || null
  const sessionToken: string | null = session ? session.getIdToken().getJwtToken() : customToken

  if (!sessionToken) {
    return next({
      name: ERouterList.LOGIN,
      query: {
        ...to.query,
      },
    })
  }

  const userHasAccess = await hasAccess(sessionToken, to.params)
  if (userHasAccess) {
    return next()
  }

  return next({
    name: ERouterList.NOT_FOUND,
    query: {
      ...to.query,
    },
  })
}
